import React, { useState } from 'react';
import { Loader2Icon, UserIcon } from 'lucide-react';
import { FormSection } from './FormSection';
import { Car } from '../types/car';
import { formatARS, formatARSCents } from '../utils/format';
import { ANNUAL_RATE, DEFAULT_INSTALLMENTS, FINANCING_TERMS, calcFinancedTotal, calcInstallment, formatRate } from '../utils/financing';
import { formatCuil, isValidCuil } from '../utils/cuil';
import { submitFinancingRequest } from '../utils/financingRequest';
import { CarSummary } from './CarSummary';
import { CreditProfile, CreditProfileFields, emptyCreditProfile, validateCreditProfile } from './CreditProfileFields';
import { FormField, inputClass } from './FormField';
import { SubmitError } from './SubmitError';
import { SuccessState } from './SuccessState';
type Applicant = {
  firstName: string;
  lastName: string;
  cuil: string;
  email: string;
  phone: string;
};
interface FinancingSimulatorProps {
  car: Car;
  onClose: () => void;
}
const MIN_DOWN = 0;
const MAX_DOWN = 90;
const formatThousands = (n: number) => n > 0 ? new Intl.NumberFormat('es-AR').format(n) : '';
export function FinancingSimulator({
  car,
  onClose
}: FinancingSimulatorProps) {
  const maxDown = Math.round(car.cashPrice * MAX_DOWN / 100);
  const clampDown = (n: number) => Math.min(maxDown, Math.max(0, Math.round(n)));
  const [downPayment, setDownPayment] = useState(clampDown(car.downPayment));
  const [term, setTerm] = useState(FINANCING_TERMS.includes(car.installments) ? car.installments : DEFAULT_INSTALLMENTS);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [applicant, setApplicant] = useState<Applicant>({
    firstName: '',
    lastName: '',
    cuil: '',
    email: '',
    phone: ''
  });
  const [errors, setErrors] = useState<Partial<Record<keyof Applicant, string>>>({});
  const [credit, setCredit] = useState<CreditProfile>(emptyCreditProfile);
  const [creditErrors, setCreditErrors] = useState<Partial<Record<keyof CreditProfile, string>>>({});
  const downPct = Math.round(downPayment / car.cashPrice * 100);
  const financed = car.cashPrice - downPayment;
  const monthly = calcInstallment(financed, term);
  const totalInstallments = calcFinancedTotal(financed, term);
  const totalToPay = downPayment + totalInstallments;
  if (status === 'success') {
    return <SuccessState title="Muchas gracias" message="En un plazo de 4 hs a 12 hs estará recibiendo su respuesta." onClose={onClose} data-id="element-250" data-mp="1aphxl7dbgc0f" />;
  }
  const update = (key: keyof Applicant) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = key === 'cuil' ? formatCuil(e.target.value) : e.target.value;
    setApplicant(prev => ({
      ...prev,
      [key]: value
    }));
    setErrors(prev => ({
      ...prev,
      [key]: undefined
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const next: Partial<Record<keyof Applicant, string>> = {};
    if (!applicant.firstName.trim()) next.firstName = 'Ingresá tu nombre';
    if (!applicant.lastName.trim()) next.lastName = 'Ingresá tu apellido';
    if (!isValidCuil(applicant.cuil)) next.cuil = 'Ingresá un CUIL válido (11 números)';
    if (!/^\S+@\S+\.\S+$/.test(applicant.email)) next.email = 'Ingresá un email válido';
    if (applicant.phone.replace(/\D/g, '').length < 8) next.phone = 'Ingresá un teléfono válido';
    const nextCredit = validateCreditProfile(credit);
    setErrors(next);
    setCreditErrors(nextCredit);
    if (Object.keys(next).length > 0 || Object.keys(nextCredit).length > 0) return;
    setStatus('loading');
    try {
      await submitFinancingRequest({
        nombre: applicant.firstName.trim(),
        apellido: applicant.lastName.trim(),
        cuil: applicant.cuil,
        telefono: applicant.phone.trim(),
        email: applicant.email.trim(),
        autoInteres: `${car.id} · ${car.brand} ${car.model} ${car.version} ${car.year}`,
        anticipo: `${formatARS(downPayment)} (${downPct}%)`,
        cuotas: `${term} cuotas de ${formatARSCents(monthly)} (total en cuotas ${formatARS(totalInstallments)})`,
        precioContado: formatARS(car.cashPrice),
        montoFinanciar: String(financed),
        valorCuota: String(monthly),
        situacionLaboral: credit.employment,
        ingresosMensuales: credit.income.replace(/\D/g, ''),
        antiguedad: credit.seniority,
        cuentaBancaria: credit.bankAccount,
        garante: credit.guarantor,
        permuta: credit.tradeIn
      });
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };
  return <form onSubmit={handleSubmit} noValidate className="space-y-6" data-id="element-251" data-mp="2v6eiropsvs1n">
      <CarSummary car={car} data-id="element-252" data-mp="2v423jjbffvsa" />

      <div data-id="element-253" data-mp="305rdswqts5fb">
        <div className="flex items-end justify-between gap-4" data-id="element-254" data-mp="305hnt95s5y9m">
          <label htmlFor="down-payment-amount" className="text-sm font-medium text-white" data-id="element-255" data-mp="302vcqzjol0mn">
            Anticipo
          </label>
          <span className="text-xs tabular-nums text-muted" data-id="element-256" data-mp="15qf09h0akq9c">{downPct}% del precio contado</span>
        </div>
        <div className="relative mt-2" data-id="element-257" data-mp="y8qxwcvae2z1">
          <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-muted" data-id="element-258" data-mp="y9s8yhcaypgr">$</span>
          <input id="down-payment-amount" inputMode="numeric" className={`${inputClass} pl-7 text-base font-semibold tabular-nums`} value={formatThousands(downPayment)} placeholder="0" onChange={e => setDownPayment(clampDown(Number(e.target.value.replace(/\D/g, '')) || 0))} data-id="element-259" data-mp="ohvy05p4b4v4" />
        </div>
        <input type="range" aria-label="Ajustar anticipo" min={0} max={maxDown} step={Math.max(1000, Math.round(car.cashPrice / 200 / 1000) * 1000)} value={downPayment} onChange={e => setDownPayment(clampDown(Number(e.target.value)))} className="mt-3 w-full accent-neon" data-id="element-260" data-mp="1c88cy0is61v7" />
        <div className="mt-1 flex justify-between text-xs text-muted" data-id="element-261" data-mp="3mygou3vm9nvm">
          <span data-id="element-262" data-mp="2f0cao165bvhy">{MIN_DOWN}%</span>
          <span data-id="element-263" data-mp="2f143x5c9th52">Máx. {MAX_DOWN}% · {formatARS(maxDown)}</span>
        </div>
      </div>

      <fieldset data-id="element-264" data-mp="2j1xvq9su3g38">
        <legend className="text-sm font-medium text-white" data-id="element-265" data-mp="2j0d1ymecg16f">Cantidad de cuotas</legend>
        <div className="mt-3 grid grid-cols-5 gap-2" data-id="element-266" data-mp="250tt1rdpklt2">
          {FINANCING_TERMS.map(t => {
          const active = t === term;
          return <button key={t} type="button" aria-pressed={active} onClick={() => setTerm(t)} className={`h-11 rounded-lg border text-sm font-semibold tabular-nums transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-neon ${active ? 'border-neon bg-neon text-ink' : 'border-line text-white hover:border-neon/60 hover:text-neon'}`} data-id="element-267" data-mp="1vc3ihr4lrpig">
                {t}
              </button>;
        })}
        </div>
      </fieldset>

      <div className="rounded-xl border border-line bg-ink p-5" aria-live="polite" data-id="element-268" data-mp="xqisanxpbg2z">
        <p className="text-sm text-muted" data-id="element-269" data-mp="xslib7yut97p">{term} cuotas fijas de</p>
        <p className="font-display text-4xl font-bold tabular-nums text-neon" data-id="element-270" data-mp="tq8lj9234ln2">{formatARSCents(monthly)}</p>
        <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-line pt-4 text-sm sm:grid-cols-4" data-id="element-271" data-mp="17q9ocu1wu356">
          <div data-id="element-272" data-mp="12oajffqiuy64">
            <dt className="text-muted" data-id="element-273" data-mp="12ryc6co57gjs">Anticipo</dt>
            <dd className="font-semibold tabular-nums text-white" data-id="element-274" data-mp="1gobmn1u9ehor">{formatARS(downPayment)}</dd>
          </div>
          <div data-id="element-275" data-mp="1cmzzd2yuy8jq">
            <dt className="text-muted" data-id="element-276" data-mp="1cnjbg31tu2pu">Monto financiado</dt>
            <dd className="font-semibold tabular-nums text-white" data-id="element-277" data-mp="1qfg180g0w0j2">{formatARS(financed)}</dd>
          </div>
          <div data-id="element-278" data-mp="1lh4p1j299dxo">
            <dt className="text-muted" data-id="element-279" data-mp="1lgmtyprbx5rc">Total en cuotas</dt>
            <dd className="font-semibold tabular-nums text-white" data-id="element-280" data-mp="20cug3vmash99">{formatARS(totalInstallments)}</dd>
          </div>
          <div data-id="element-281" data-mp="1vfc39u55thvz">
            <dt className="text-muted" data-id="element-282" data-mp="1vfkj9uwkx3be">Total a pagar</dt>
            <dd className="font-semibold tabular-nums text-white" data-id="element-283" data-mp="g26xxt4x39w8">{formatARS(totalToPay)}</dd>
          </div>
        </dl>
        <p className="mt-4 text-xs leading-5 text-muted" data-id="element-284" data-mp="l08g8buir56n">
          Total en cuotas = monto financiado + {formatRate(ANNUAL_RATE)} por cada año de plazo ({term / 12}{' '}
          {term === 12 ? 'año' : 'años'}), dividido en {term} cuotas fijas en pesos. Total a pagar = anticipo + cuotas. Valores
          aproximados, sujetos a confirmación de La Financiera.
        </p>
      </div>

      <FormSection icon={UserIcon} title="Tus datos para la solicitud" description="Los usamos para preparar tu propuesta de financiación." data-id="element-285" data-mp="2a0kwfx29j10">
        <div className="grid gap-4 sm:grid-cols-2" data-id="element-286" data-mp="78bucpjg1rip">
          <FormField label="Nombre" htmlFor="fs-first-name" error={errors.firstName} data-id="element-287" data-mp="c0v93ptvasp3">
            <input id="fs-first-name" className={inputClass} value={applicant.firstName} onChange={update('firstName')} autoComplete="given-name" data-id="element-288" data-mp="3aqlkx77pt1rz" />
          </FormField>
          <FormField label="Apellido" htmlFor="fs-last-name" error={errors.lastName} data-id="element-289" data-mp="3fnc1dvnflzdx">
            <input id="fs-last-name" className={inputClass} value={applicant.lastName} onChange={update('lastName')} autoComplete="family-name" data-id="element-290" data-mp="1wtixyvjqsrre" />
          </FormField>
          <FormField label="CUIL" htmlFor="fs-cuil" error={errors.cuil} data-id="element-291" data-mp="1nsug1pr8a798">
            <input id="fs-cuil" inputMode="numeric" placeholder="20-12345678-9" className={inputClass} value={applicant.cuil} onChange={update('cuil')} data-id="element-292" data-mp="1iuszpcl6sm1g" />
          </FormField>
          <FormField label="Teléfono" htmlFor="fs-phone" error={errors.phone} data-id="element-293" data-mp="192mzxgop10bp">
            <input id="fs-phone" type="tel" placeholder="11 5555 5555" className={inputClass} value={applicant.phone} onChange={update('phone')} autoComplete="tel" data-id="element-294" data-mp="153nu7tv50zj7" />
          </FormField>
          <FormField label="Email" htmlFor="fs-email" error={errors.email} className="sm:col-span-2" data-id="element-295" data-mp="q6f04seg9hns">
            <input id="fs-email" type="email" className={inputClass} value={applicant.email} onChange={update('email')} autoComplete="email" data-id="element-296" data-mp="3gh2xy6qhoohl" />
          </FormField>
        </div>
      </FormSection>

      <CreditProfileFields values={credit} errors={creditErrors} onChange={(key, value) => {
      setCredit(prev => ({
        ...prev,
        [key]: value
      }));
      setCreditErrors(prev => ({
        ...prev,
        [key]: undefined
      }));
    }} data-id="element-297" data-mp="3l9w6l5p2wgyy" />

      {status === 'error' && <SubmitError data-id="element-298" data-mp="36l9gqa1ts6th" />}

      <button type="submit" disabled={status === 'loading'} className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-neon text-sm font-semibold text-ink transition-colors duration-150 hover:bg-neon-bright disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-neon focus-visible:ring-offset-2 focus-visible:ring-offset-panel" data-id="element-299" data-mp="36lrlxw6gtvdj">
        {status === 'loading' && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-300" data-mp="tds0zo0fs196" />}
        {status === 'loading' ? 'Enviando…' : status === 'error' ? 'Reintentar' : 'Enviar solicitud'}
      </button>
    </form>;
}